import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'About the Book', path: '/about' },
    { name: 'Who It\'s For', path: '/audience' },
    { name: 'What You\'ll Learn', path: '/learn' },
    { name: 'Author', path: '/author' },
  ];

  const guidesLinks = [
    { name: 'HTML & CSS Basics', path: '/guides/html-css-for-beginners' },
    { name: 'WordPress Setup', path: '/guides/wordpress-setup' },
    { name: 'Website Ownership', path: '/guides/website-ownership' },
    { name: 'Accessibility', path: '/guides/accessibility' },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans">
      {/* Navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="font-bold text-xl tracking-tight text-slate-900 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-blue-600 h-6 w-6" />
                <span>Blueprint<span className="text-blue-600">Foundation</span></span>
              </Link>
            </div>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-8 items-center">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'text-blue-600' : 'text-slate-600 hover:text-blue-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="relative group">
                <button className="text-sm font-medium text-slate-600 hover:text-blue-600 flex items-center gap-1">
                  Guides <SafeIcon icon={FiIcons.FiChevronDown} className="h-4 w-4" />
                </button>
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-1">
                    {guidesLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <Link
                to="/buy"
                className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                Buy the Book
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-600 hover:text-slate-900 focus:outline-none"
              >
                <SafeIcon icon={isMobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50"
                >
                  {link.name}
                </Link>
              ))}
              <div className="px-3 py-2 text-base font-semibold text-slate-900">Free Guides</div>
              {guidesLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 pl-6 rounded-md text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50"
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/buy"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center mt-4 bg-blue-600 text-white px-5 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Buy the Book
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <span className="font-bold text-xl tracking-tight text-white flex items-center gap-2 mb-4">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-blue-500 h-6 w-6" />
                Website Blueprint Foundation
              </span>
              <p className="text-sm text-slate-400 max-w-md">
                A practical step-by-step guide to domains, hosting, HTML, CSS, WordPress, accessibility, and launch readiness for beginners, freelancers, and business owners.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Learn More</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About the Book</Link></li>
                <li><Link to="/audience" className="hover:text-white transition-colors">Who It's For</Link></li>
                <li><Link to="/learn" className="hover:text-white transition-colors">What You'll Learn</Link></li>
                <li><Link to="/author" className="hover:text-white transition-colors">About Andrei Ciomîrtan</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Free Guides</h3>
              <ul className="space-y-2 text-sm">
                {guidesLinks.map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className="hover:text-white transition-colors">{link.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Andrei Ciomîrtan. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;